import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import {
  ApiResponse,
  JobCategory,
  JobCategoryCreatePayload,
  JobCategoryUpdatePayload,
  PaginatedResponse,
} from '@/types'

type JobCategoryImageOptions = {
  logoFile?: File | null
  removeLogo?: boolean
}

function toCategoryFormData(payload: JobCategoryCreatePayload | JobCategoryUpdatePayload, options?: JobCategoryImageOptions) {
  const formData = new FormData()
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, String(value))
    }
  })
  if (options?.logoFile) {
    formData.append('logo_file', options.logoFile)
  }
  if (options?.removeLogo) {
    formData.append('remove_logo', 'true')
  }
  return formData
}

export const categoriesApi = {
  getCategories: async (params?: { include_inactive?: boolean; kind?: 'job' | 'project' }): Promise<PaginatedResponse<JobCategory>> => {
    const queryParams: Record<string, unknown> = {}
    if (params?.include_inactive) {
      queryParams.include_inactive = true
    }
    if (params?.kind) {
      queryParams.kind = params.kind
    }
    const response = await api.get<ApiResponse<PaginatedResponse<JobCategory>> | PaginatedResponse<JobCategory>>(
      API_PATHS.categories.list,
      {
        params: Object.keys(queryParams).length ? queryParams : undefined,
      },
    )
    return 'data' in response.data ? response.data.data : response.data
  },

  getCategory: async (id: string): Promise<JobCategory> => {
    const response = await api.get<ApiResponse<JobCategory>>(API_PATHS.categories.detail(id))
    return response.data.data
  },

  createCategory: async (payload: JobCategoryCreatePayload, options?: JobCategoryImageOptions): Promise<JobCategory> => {
    const requestBody = options?.logoFile ? toCategoryFormData(payload, options) : payload
    const response = await api.post<ApiResponse<JobCategory>>(
      API_PATHS.categories.create,
      requestBody,
      requestBody instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    return response.data.data
  },

  updateCategory: async (id: string, payload: JobCategoryUpdatePayload, options?: JobCategoryImageOptions): Promise<JobCategory> => {
    const shouldUseFormData = Boolean(options?.logoFile || options?.removeLogo)
    const requestBody = shouldUseFormData ? toCategoryFormData(payload, options) : payload
    const response = await api.post<ApiResponse<JobCategory>>(
      API_PATHS.categories.update(id),
      requestBody,
      requestBody instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    return response.data.data
  },

  deactivateCategory: async (id: string): Promise<JobCategory> => {
    const response = await api.post<ApiResponse<JobCategory>>(API_PATHS.categories.deactivate(id))
    return response.data.data
  },

  activateCategory: async (id: string): Promise<JobCategory> => {
    const response = await api.post<ApiResponse<JobCategory>>(API_PATHS.categories.activate(id))
    return response.data.data
  },

  deleteCategory: async (id: string): Promise<void> => {
    await api.delete(API_PATHS.categories.delete(id))
  },
}

export default categoriesApi
